import apiClient from './apiClient';

/**
 * Tạo đơn hàng mới.
 * @param {object} orderInput - Dữ liệu đơn hàng từ frontend.
 * @returns {Promise<object>} - Promise giải quyết với thông tin đơn hàng đã tạo.
 */
export const createOrder = async (orderInput) => {
  const payloadForBackend = {
    userId: orderInput.userId || null,
    data: {
      items: orderInput.data?.items || [],
      shippingInfo: {
        name: orderInput.data?.shippingInfo?.name || '',
        phone: orderInput.data?.shippingInfo?.phone || '',
        email: orderInput.data?.shippingInfo?.email || '',
        address: orderInput.data?.shippingInfo?.address || '',
        shippingType: orderInput.data?.shippingInfo?.shippingType || 'delivery',
        orderNotes: orderInput.data?.shippingInfo?.orderNotes || '',
        pickupStore: orderInput.data?.shippingInfo?.pickupStore || '',
      },
      paymentMethod: orderInput.data?.paymentMethod || 'cod',
      wantsEinvoice: orderInput.data?.wantsEinvoice || false,
      subtotal: orderInput.data?.subtotal || 0,
      shippingCost: orderInput.data?.shippingCost || 0,
      discountCode: orderInput.data?.discountCode || '',
      discountAmount: orderInput.data?.discountAmount || 0,
      totalAmount: orderInput.data?.totalAmount || 0,
    },
  };

  console.log('[OrderService] POST /data/order/submit - Sending request payload:', payloadForBackend);

  try {
    const response = await apiClient.post('/data/order/submit', payloadForBackend);
    console.log('[OrderService] POST /data/order/submit - Response:', response.data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Không thể tạo đơn hàng.';
    console.error('[OrderService] POST /data/order/submit - Error:', errorMessage, error.response?.data);
    throw new Error(errorMessage);
  }
};

/**
 * Lấy danh sách đơn hàng của một người dùng.
 * @param {number | string} userId - ID của người dùng.
 * @returns {Promise<Array>} - Promise giải quyết với mảng các đơn hàng.
 */
export const fetchOrdersByUserIdFromBackend = async (userId) => {
  if (userId === undefined || userId === null) {
    console.error('[OrderApiService] fetchOrdersByUserIdFromBackend - Error: userId is missing.');
    throw new Error('userId là bắt buộc để lấy danh sách đơn hàng.');
  }
  console.log(`[OrderApiService] GET /data/order/${userId} - Sending request`);
  try {
    const response = await apiClient.get(`/data/order/${userId}`);
    console.log(`[OrderApiService] GET /data/order/${userId} - Response:`, response.data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Không thể lấy danh sách đơn hàng.';
    console.error(`[OrderApiService] GET /data/order/${userId} - Error:`, errorMessage, error.response || error);
    throw new Error(errorMessage);
  }
};

/**
 * Lấy chi tiết một đơn hàng theo ID.
 * @param {string} orderId - Mã đơn hàng.
 * @param {number | string} userId - ID của người dùng.
 * @returns {Promise<object>} - Promise giải quyết với object đơn hàng.
 */
export const getOrderById = async (orderId, userId) => {
  if (!orderId || !userId) {
    console.error('[OrderService] getOrderById - Error: orderId and userId are required.');
    throw new Error('orderId và userId là bắt buộc.');
  }
  console.log(`[OrderService] GET /data/order/${orderId}/${userId} - Sending request`);
  try {
    const response = await apiClient.get(`/data/order/${orderId}/${userId}`);
    console.log(`[OrderService] GET /data/order/${orderId}/${userId} - Response:`, response.data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Không thể lấy chi tiết đơn hàng.';
    console.error(`[OrderService] GET /data/order/${orderId}/${userId} - Error:`, errorMessage, error.response?.data);
    throw new Error(errorMessage);
  }
};

/**
 * Cập nhật trạng thái đơn hàng.
 * @param {string} orderId - Mã đơn hàng.
 * @param {string} status - Trạng thái mới (processing, shipped, delivered, cancelled).
 * @param {number | string} userId - ID của người dùng.
 * @returns {Promise<object>} - Promise giải quyết với đơn hàng đã cập nhật.
 */
export const updateOrderStatus = async (orderId, status, userId) => {
  if (!orderId || !status || !userId) {
    console.error('[OrderService] updateOrderStatus - Error: orderId, status, and userId are required.');
    throw new Error('orderId, status và userId là bắt buộc.');
  }
  console.log(`[OrderService] PATCH /data/order/${orderId}/${userId}/status - Sending request with status: ${status}`);
  try {
    const response = await apiClient.patch(`/data/order/${orderId}/${userId}/status`, { status });
    console.log(`[OrderService] PATCH /data/order/${orderId}/${userId}/status - Response:`, response.data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Không thể cập nhật trạng thái đơn hàng.';
    console.error(`[OrderService] PATCH /data/order/${orderId}/${userId}/status - Error:`, errorMessage, error.response?.data);
    throw new Error(errorMessage);
  }
};

/**
 * Hủy đơn hàng.
 * @param {string} orderId - Mã đơn hàng.
 * @param {number | string} userId - ID của người dùng.
 * @returns {Promise<object>} - Promise giải quyết với đơn hàng đã hủy.
 */
export const cancelOrder = async (orderId, userId) => {
  if (!orderId || !userId) {
    console.error('[OrderService] cancelOrder - Error: orderId and userId are required.');
    throw new Error('orderId và userId là bắt buộc.');
  }
  console.log(`[OrderService] DELETE /data/order/${orderId}/${userId} - Sending request`);
  try {
    const response = await apiClient.delete(`/data/order/${orderId}/${userId}`);
    console.log(`[OrderService] DELETE /data/order/${orderId}/${userId} - Response:`, response.data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Không thể hủy đơn hàng.';
    console.error(`[OrderService] DELETE /data/order/${orderId}/${userId} - Error:`, errorMessage, error.response?.data);
    throw new Error(errorMessage);
  }
};